import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * The section wrapper of a sticky announcement bar pinned above the header
   * @type {HTMLElement | null}
   */
  #stickyAnnouncement = null;

  /**
   * Height (rounded down) of the sticky announcement bar the header sticks beneath, 0 when there isn't one
   * @type {number}
   */
  #stickyOffset = 0;

  /**
   * Keeps the global `--sticky-announcement-height` custom property up to date,
   * which the header and other sticky components offset themselves by
   */
  #announcementResizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--sticky-announcement-height', `${height}px`);

    // Round down: the header's resting top can be fractional (e.g. a 0.5px divider), and rounding up
    // would put it above the observed edge, marking it stuck (and opaque) while still at rest
    const offset = Math.floor(height);
    if (offset === this.#stickyOffset) return;

    this.#stickyOffset = offset;
    this.#resetStickyPosition();
  });

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    /** @type {IntersectionObserverInit} */
    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    // Shrink the observed viewport by the sticky announcement bar, so the header counts as
    // stuck/offscreen at its bottom edge. An explicit root keeps rootMargin working in iframes.
    if (this.#stickyOffset) {
      config.root = document;
      config.rootMargin = `-${this.#stickyOffset}px 0px 0px 0px`;
    }

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Recreates the sticky position observer, e.g. when the sticky offset changes
   */
  #resetStickyPosition() {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  }

  /**
   * Finds an announcement bar set to stick alongside the header and tracks its height.
   * Only applies when it sits above the header in the header group.
   */
  #setupStickyAnnouncement = () => {
    const section = document.querySelector('#header-group .announcement-bar[data-sticky]')?.closest('.shopify-section');
    const isAboveHeader =
      section instanceof HTMLElement &&
      Boolean(section.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING);
    const stickyAnnouncement = isAboveHeader ? section : null;

    if (stickyAnnouncement === this.#stickyAnnouncement) return;

    this.#announcementResizeObserver.disconnect();
    this.#stickyAnnouncement = stickyAnnouncement;

    if (stickyAnnouncement) {
      // Fires immediately on observe, which sets the offset
      this.#announcementResizeObserver.observe(stickyAnnouncement);
    } else if (this.#stickyOffset) {
      this.#stickyOffset = 0;
      document.body.style.removeProperty('--sticky-announcement-height');
      this.#resetStickyPosition();
    }
  };

  /**
   * Re-checks the sticky announcement bar once the theme editor has re-rendered a section
   */
  #handleSectionChange = () => requestAnimationFrame(this.#setupStickyAnnouncement);

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    const overflowEvent = /** @type {OverflowMinimumEvent} */ (event);
    this.#updateMenuVisibility(overflowEvent.detail.minimumReached);
  };

  /** @param {MouseEvent} event */
  #handlePromoBannerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const banner = target.closest('[data-nav-promo-banner]');
    if (!(banner instanceof HTMLElement)) return;

    const windowWithDataLayer = /** @type {Window & { dataLayer?: Array<Record<string, string>> }} */ (window);
    const dataLayer = Array.isArray(windowWithDataLayer.dataLayer) ? windowWithDataLayer.dataLayer : [];
    windowWithDataLayer.dataLayer = dataLayer;
    dataLayer.push({
      event: 'navigation_banner_click',
      navigation_item: banner.dataset.navPromoItem || '',
      banner_title: banner.dataset.navPromoTitle || '',
      banner_link: banner.dataset.navPromoLink || '',
      header_sticky_state: this.dataset.stickyState || 'default',
    });
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('nav-desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('nav-desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= this.#stickyOffset;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= this.#stickyOffset) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', /** @type {EventListener} */ (this.#handleOverflowMinimum));
    this.addEventListener('click', this.#handlePromoBannerClick);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
      this.#setupStickyAnnouncement();
      document.addEventListener('shopify:section:load', this.#handleSectionChange);
      document.addEventListener('shopify:section:unload', this.#handleSectionChange);

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#announcementResizeObserver.disconnect();
    this.#stickyAnnouncement = null;
    this.#stickyOffset = 0;
    document.body.style.removeProperty('--sticky-announcement-height');
    document.removeEventListener('shopify:section:load', this.#handleSectionChange);
    document.removeEventListener('shopify:section:unload', this.#handleSectionChange);
    this.removeEventListener('overflowMinimum', /** @type {EventListener} */ (this.#handleOverflowMinimum));
    this.removeEventListener('click', this.#handlePromoBannerClick);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * Moves each pre-rendered nav promo banner (rendered once, hidden, by its
 * `header-mega-menu-banner` block) into the matching placeholder slot inside
 * the mega menu / mobile drawer for the navigation item it targets.
 *
 * This exists because Shopify theme blocks don't expose `block.settings` when
 * read via `section.blocks` from outside the block's own template, so the
 * navigation-item-to-menu matching can't be done in Liquid alone.
 */
function positionNavPromoBanners() {
  const sources = document.querySelectorAll('[data-nav-promo-source]');

  sources.forEach((source) => {
    if (!(source instanceof HTMLElement)) return;

    const banner = source.firstElementChild;
    const target = source.dataset.navPromoTarget;
    const context = source.dataset.navPromoContext;
    if (!target || !banner) return;

    const slot = document.querySelector(
      `[data-nav-promo-slot="${target}"][data-nav-promo-context="${context}"]`
    );
    if (!slot) return;

    slot.replaceChildren(banner);
  });
}

onDocumentLoaded(() => {
  positionNavPromoBanners();

  // The theme editor replaces a section's markup wholesale rather than morphing
  // it, so the staging area comes back populated and the banners need placing
  // again. Safe to run for any section: it no-ops once the sources are empty.
  document.addEventListener('shopify:section:load', () => positionNavPromoBanners());

  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // On pages where the header is lazily hydrated, its mega menu markup — and so
  // every banner slot — only exists after hydration. The pass above finds no
  // slots on those pages, so place again once the section flags itself hydrated.
  if (headerGroup) {
    new MutationObserver(() => positionNavPromoBanners()).observe(headerGroup, {
      attributes: true,
      attributeFilter: ['data-hydrated'],
      subtree: true,
    });
  }

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }

          // Section hydration (e.g. after cart/localization sync) replaces the
          // header's markup, so re-run banner placement against the fresh DOM.
          positionNavPromoBanners();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
